import {
    Form,
    FormLayout,
    TextField,
    Button,
    ResourceItem,
    ResourceList,
    LegacyCard,
    Layout,
    Toast,
    Text, Badge,
} from "@shopify/polaris";
import {useState, useCallback, useEffect, useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import Api from '../../helpers/api';

function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
}

const SearchCustomer = props => {
    const history = useHistory();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [errors, setErrors] = useState(null);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({
        active: false,
        message: ''
    });

    const queryString = useQuery();

    const handleSubmit = () => {
        if (query == '') {
            setErrors('Please type something in the search field.')
            return;
        };

        history.push('/customers?' + (new URLSearchParams({q: query})));
        loadCustomers(query, true);
    }

    const loadCustomers = (q, canRedirect) => {
        setErrors(null);
        setLoading(true);
        setResults([]);

        Api.findCustomer({query: q})
            .then(({ data: result }) => {
                if (result.data.length === 1 && canRedirect) {
                    const customerData = result.data[0];
                    assignCustomer(customerData);
                } else if (result.data.length === 0) {
                    setToast({active: true, 'message': 'There are no results matching your search.'});
                } else {
                    setResults(result.data);
                }
            })
            .catch(() => {
                setToast({active: true, 'message': 'Error searching Companies. Please try again.'});
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (queryString.get('q') == '' || queryString.get('q') == null) {
            return;
        }

        setQuery(queryString.get('q'));
        loadCustomers(queryString.get('q'), false);
    }, []);

    const assignCustomer = customer => {
        props.onCustomerSelect(customer);
        setResults([]);
        setQuery('');
    }

    const handleQueryChange = useCallback(
        (value) => setQuery(value),
        [],
    );

    const toastMarkup = toast.active ? (
        <Toast content={toast.message} onDismiss={() => {setToast({message: '', active: false})}} />
      ) : null;

    const customerNameText = (customer) => {
        let name = '';

        if (customer.account_number && customer.account_number.length > 0) {
            name += `#${customer.account_number}`;
        }

        const firstName = customer.shopify_company_name;

        if (firstName && firstName.length > 0) {
            name += ` - ${firstName}`;
        }

        return name;
    }

    return (
        <Layout>
        <Layout.Section>
            <Form onSubmit={handleSubmit}>
                <FormLayout>
                    <TextField
                        value={query}
                        onChange={handleQueryChange}
                        label="Search"
                        type="text"
                        error={errors}
                        helpText={
                            <span>Search by Phone or Company Account Number</span>
                        }
                        connectedRight={<Button loading={loading} submit  variant="primary">Search</Button>}
                    />
                </FormLayout>
            </Form>
        </Layout.Section>

        {results.length > 0 && (
            <Layout.Section>
                <LegacyCard title="Search Results">
                    <ResourceList
                        resourceName={{ singular: 'company', plural: 'companies' }}
                        items={results}
                        renderItem={(item, index) => {
                            return (
                                <ResourceItem
                                    id={item.id}
                                    key={index}
                                    onClick={event => { assignCustomer(item) }}
                                    accessibilityLabel={`View details for account ${item.account_number ?? ''}`}
                                >
                                    <h3>
                                        <Text  variant="bodyMd" as="span" fontWeight="semibold">{customerNameText(item)}</Text>
                                    </h3>
                                    { item.shopify_users && item.shopify_users.length > 0 && (
                                        item.shopify_users.map((shopifyUser) => <Badge tone={"info"}>{shopifyUser.name}</Badge>)
                                    )}
                                    { (!item.shopify_users || item.shopify_users.length == 0) && (
                                        <Badge tone={"warning"}>No Sales Rep</Badge>
                                    )}
                                    <div>{`Phone: ${item.additional_data.phone} - Email: ${item.email}`}</div>
                                </ResourceItem>
                            );
                        }}
                    />
                </LegacyCard>
            </Layout.Section>
        )}
        {toastMarkup}
    </Layout>
    );
}

export default SearchCustomer;
